/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * AI next steps for one lead or deal.
 *
 *   const nba = useAiNextActions("deals", dealId);
 *   nba.enabled      this user may use the AI Assistant (module + seat + credits)
 *   nba.suggestion   { summary, actions: [{ title, why, type, priority, due_in_days }],
 *                      generated_at, stale } or null when none has been made
 *   nba.suggest()    make (or remake) them now; costs credits
 *
 * Reading the kept suggestion is free and happens once per record. Nothing
 * here, or in what the backend sends, says which provider or model is used.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import useAiAssistantStatus from "hooks/useAiAssistantStatus";
import { getApiErrorMessage } from "utils/apiErrors";

export default function useAiNextActions(objectApiName, recordId) {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const { canUseAiAssistant, refresh: refreshStatus } = useAiAssistantStatus();
  const enabled = Boolean(!entitlementsLoading && isModuleEnabled("AI_ASSISTANT") && canUseAiAssistant);
  const ready = Boolean(enabled && recordId && isAuthReady && isAuthenticated && axiosInstance);
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const path = `/ai/next-actions/${objectApiName}/${recordId}`;

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance
      .get(path)
      .then((res) => {
        if (!cancelled) setSuggestion(res.data?.suggestion || null);
      })
      // A failed read just shows the empty state; making one still works.
      .catch(() => {})
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, path, axiosInstance]);

  const suggest = useCallback(async () => {
    setWorking(true);
    setError("");
    try {
      const res = await axiosInstance.post(path);
      if (mounted.current) setSuggestion(res.data?.suggestion || null);
      return true;
    } catch (err) {
      if ([402, 403].includes(err?.response?.status)) refreshStatus();
      if (mounted.current) {
        setError(getApiErrorMessage(err, "The AI Assistant could not suggest next steps. Please try again."));
      }
      return false;
    } finally {
      if (mounted.current) setWorking(false);
    }
  }, [axiosInstance, path, refreshStatus]);

  return { enabled, suggestion, loading, working, error, suggest };
}
